import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, ArrowUpRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSiteSettings } from "@/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services/international-tax-transfer-pricing", label: "Transfer Pricing" },
  { to: "/services/gcc-india-back-office", label: "India Back Office" },
  { to: "/services/family-office", label: "Family Office" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("tv-theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("tv-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export function ThemeToggle() {
  const [dark, setDark] = useTheme();
  return (
    <button
      type="button"
      onClick={() => setDark(!dark)}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle"
      className="flex h-9 w-9 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors duration-200 hover:border-accent hover:text-accent"
    >
      {dark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

export function Header({ settings }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const calendly = settings?.calendly_url || "https://calendly.com/pro-truverse/30min";

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl" data-testid="site-header">
      <div className="container-site flex h-16 items-center justify-between gap-6 lg:h-20">
        <Link to="/" className="flex items-end gap-2" data-testid="brand-link" aria-label="TruVerse Middle East home">
          <img src="/logo.webp" alt="TruVerse" className="h-10 w-auto lg:h-12" data-testid="brand-logo" />
          <span className="pb-0.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#1C398E] dark:text-[#7C9BF5]">Middle East</span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a href={calendly} target="_blank" rel="noopener noreferrer" className="hidden lg:block" data-testid="header-book-consultation-btn">
            <Button className="rounded-sm bg-accent px-5 text-[13px] font-semibold text-accent-foreground transition-colors duration-200 hover:bg-accent/85">
              Book a consultation
            </Button>
          </a>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-sm border border-border lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-border bg-background px-6 py-6 lg:hidden" aria-label="Mobile" data-testid="mobile-nav">
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className="text-sm font-medium text-foreground" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
            <a href={calendly} target="_blank" rel="noopener noreferrer" data-testid="mobile-book-consultation-btn">
              <Button className="mt-2 w-full rounded-sm bg-accent text-accent-foreground">Book a consultation</Button>
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}

export function Footer({ settings }) {
  const s = settings || {};
  const social = s.social || {};
  const socials = [
    { label: "LinkedIn", href: social.linkedin },
    { label: "X", href: social.x },
    { label: "Instagram", href: social.instagram },
    { label: "YouTube", href: social.youtube },
  ];
  return (
    <footer className="border-t border-border bg-ink" data-testid="site-footer">
      <div className="container-site grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-end gap-2">
            <img src="/logo.webp" alt="TruVerse" className="h-8 w-auto brightness-0 invert" data-testid="footer-logo" />
            <span className="pb-0.5 text-[9px] font-semibold uppercase tracking-[0.3em] text-accent">Middle East</span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-ink-muted">
            {s.positioning || "Trusted depth. Personal attention. One India UAE team."}
          </p>
          <div className="mt-6 flex gap-4">
            {socials.map((soc) => (
              <a key={soc.label} href={soc.href || "#"} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${soc.label.toLowerCase()}`} className="text-xs font-medium uppercase tracking-widest text-ink-muted transition-colors duration-200 hover:text-accent">
                {soc.label}
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Services</h3>
          <ul className="mt-5 space-y-3 text-sm text-ink-muted">
            <li><Link to="/services/international-tax-transfer-pricing" data-testid="footer-link-tax" className="transition-colors duration-200 hover:text-accent">Transfer Pricing</Link></li>
            <li><Link to="/services/gcc-india-back-office" data-testid="footer-link-gcc" className="transition-colors duration-200 hover:text-accent">India Back Office</Link></li>
            <li><Link to="/services/family-office" data-testid="footer-link-family-office" className="transition-colors duration-200 hover:text-accent">Family Office</Link></li>
            <li><Link to="/resources" data-testid="footer-link-resources" className="transition-colors duration-200 hover:text-accent">Guides & Checklists</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Offices</h3>
          <ul className="mt-5 space-y-4 text-sm leading-relaxed text-ink-muted">
            <li data-testid="footer-address-india"><span className="block font-semibold text-ink-fg">Chennai (Headquarters)</span>{s.address_india}</li>
            <li data-testid="footer-address-dubai"><span className="block font-semibold text-ink-fg">Dubai</span>Dubai, United Arab Emirates</li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Contact</h3>
          <ul className="mt-5 space-y-3 text-sm text-ink-muted">
            <li><a href={`mailto:${s.email || "pro@truverse.in"}`} data-testid="footer-email" className="transition-colors duration-200 hover:text-accent">{s.email || "pro@truverse.in"}</a></li>
            <li data-testid="footer-phone-india">{s.phone_india || "+91 72002 40550"}</li>
            <li data-testid="footer-phone-uae">{s.phone_uae || "[UAE phone, add in admin]"}</li>
          </ul>
          <ul className="mt-6 space-y-2 text-xs text-ink-muted">
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-accent">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-accent">Terms of Use</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ink">
        <div className="container-site flex flex-col gap-2 py-6 text-xs text-ink-muted md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-disclaimer">Content is general information, not tax or legal advice.</p>
          <p>© {new Date().getFullYear()} TruVerse Middle East. Part of TruVerse, Chennai.</p>
        </div>
      </div>
    </footer>
  );
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("tv-consent")) setVisible(true);
  }, []);
  if (!visible) return null;
  const choose = (v) => {
    localStorage.setItem("tv-consent", v);
    setVisible(false);
  };
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border bg-card/95 backdrop-blur-xl" role="dialog" aria-label="Cookie consent" data-testid="cookie-banner">
      <div className="container-site flex flex-col items-start gap-4 py-5 md:flex-row md:items-center md:justify-between">
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          We use necessary cookies to run this site. Analytics cookies load only with your consent.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-sm" onClick={() => choose("necessary")} data-testid="cookie-necessary-btn">
            Necessary only
          </Button>
          <Button className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/85" onClick={() => choose("all")} data-testid="cookie-accept-btn">
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WhatsAppFloat({ settings }) {
  const number = settings?.whatsapp;
  if (!number) return null;
  return (
    <a
      href={`https://wa.me/${number.replace(/[^0-9]/g, "")}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      data-testid="whatsapp-float-btn"
      className="fixed bottom-6 right-6 z-50 flex h-13 w-13 items-center justify-center rounded-full bg-accent p-3.5 text-accent-foreground shadow-lg shadow-accent/25 transition-transform duration-200 hover:scale-105"
    >
      <MessageCircle size={22} />
    </a>
  );
}

export default function SiteLayout() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    getSiteSettings().then(setSettings).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <Header settings={settings} />
      <main>
        <Outlet context={{ settings }} />
      </main>
      <Footer settings={settings} />
      <WhatsAppFloat settings={settings} />
      <CookieBanner />
    </div>
  );
}

export function CalendlyCta({ label = "Book a consultation", testId = "cta-book-consultation", variant = "gold", className = "" }) {
  return (
    <a href="https://calendly.com/pro-truverse/30min" target="_blank" rel="noopener noreferrer" data-testid={testId} className={className}>
      <Button
        className={
          variant === "gold"
            ? "group rounded-sm bg-accent px-7 py-6 text-sm font-semibold text-accent-foreground transition-colors duration-200 hover:bg-accent/85"
            : "group rounded-sm border border-border bg-transparent px-7 py-6 text-sm font-semibold text-foreground transition-colors duration-200 hover:border-accent hover:text-accent"
        }
        variant={variant === "gold" ? "default" : "outline"}
      >
        {label}
        <ArrowUpRight size={16} className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Button>
    </a>
  );
}
